import type { ListElements } from '@/typings/customilyElements/layer';

type CollectionResult = {
    items: ListElements[];
    isInRange: boolean;
};

export const isChildOf = (child: ListElements, parent: ListElements): boolean => {
    const isChild = (item: ListElements): boolean => {
        if (item.element.uuid === parent.element.uuid) {
            return true;
        }

        if (item.children) {
            for (const child of item.children) {
                if (isChild(child)) {
                    return true;
                }
            }
        }

        return false;
    };

    return isChild(child);
};

export const compareArrays = (pathA: number[], pathB: number[]): number => {
    for (let i = 0; i < Math.min(pathA.length, pathB.length); i++) {
        if (pathA[i] !== pathB[i]) return pathA[i] - pathB[i];
    }
    return pathA.length - pathB.length;
};

export const findElementPath = (
    elements: ListElements[],
    targetUuid: string,
    currentPath: number[] = []
): number[] | null => {
    for (let i = 0; i < elements.length; i++) {
        const element = elements[i];
        const path = [...currentPath, i];

        if (element.element.uuid === targetUuid) return path;

        if (element.children?.length) {
            const childPath = findElementPath(element.children, targetUuid, path);
            if (childPath) return childPath;
        }
    }
    return null;
};

export const collectElements = (tree: ListElements[], startPath: number[], endPath: number[]): ListElements[] => {
    const comparePaths = (path1: number[], path2: number[]): number => {
        const minLength = Math.min(path1.length, path2.length);
        for (let i = 0; i < minLength; i++) {
            if (path1[i] !== path2[i]) {
                return path1[i] - path2[i];
            }
        }
        return path1.length - path2.length;
    };

    const shouldSwapPaths = comparePaths(startPath, endPath) > 0;
    const [effectiveStart, effectiveEnd] = shouldSwapPaths ? [endPath, startPath] : [startPath, endPath];

    const isPathInRange = (currentPath: number[], start: number[], end: number[]): boolean => {
        return comparePaths(currentPath, start) >= 0 && comparePaths(currentPath, end) <= 0;
    };

    const collectInRange = (item: ListElements, currentPath: number[] = []): CollectionResult => {
        const result: CollectionResult = {
            items: [],
            isInRange: false
        };

        const isCurrentInRange = isPathInRange(currentPath, effectiveStart, effectiveEnd);

        if (!item.children?.length) {
            if (isCurrentInRange) {
                result.items = [item];
                result.isInRange = true;
            }
            return result;
        }

        const childResults = item.children.map((child, index) => collectInRange(child, [...currentPath, index]));

        const allChildrenInRange = childResults.every((r) => r.isInRange);
        const anyChildInRange = childResults.some((r) => r.isInRange);

        if (allChildrenInRange && isCurrentInRange) {
            result.items = [item];
            result.isInRange = true;
        } else if (anyChildInRange) {
            result.items = childResults.flatMap((r) => r.items);
            result.isInRange = true;
        } else if (isCurrentInRange) {
            result.items = [item];
            result.isInRange = true;
        }

        return result;
    };

    return tree.reduce((acc: ListElements[], item, index) => {
        const result = collectInRange(item, [index]);
        return [...acc, ...result.items];
    }, []);
};
